import { useState } from "react";
import { SubBadge } from "@/components/admin/SectionShell";
import { Check, Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { mediaSrc } from "@/lib/media";

const isPending = (f) => f.approved === false;
const FILTERS = {
  all: { label: "All", match: () => true, empty: "No feedback yet." },
  pending: { label: "Pending", match: isPending, empty: "No pending feedback." },
  approved: { label: "Approved", match: (f) => !isPending(f), empty: "No approved feedback." },
};
const iconBtn = "grid place-items-center w-8 h-8 rounded-full border border-border hover:bg-primary hover:text-primary-foreground transition-colors";

const FeedbackMedia = ({ f }) => {
  if (f.kind === "audio" && f.media_url) return <audio controls src={mediaSrc(f.media_url)} className="w-full max-w-md" />;
  if (f.kind === "video" && f.media_url) return <video controls src={mediaSrc(f.media_url)} className="w-full max-w-md rounded-xl max-h-56" />;
  return <p className="text-foreground/90 break-words">"{f.message}"</p>;
};

const FeedbackRow = ({ f, approveFeedback, toggleFeedbackVisible, startEditFb, deleteFeedback }) => {
  const hidden = f.visible === false;
  const pending = isPending(f);
  const tone = pending ? "border-[#E0A63C]/50 bg-[#E0A63C]/5" : hidden ? "border-border/40 bg-muted/40 opacity-70" : "border-border bg-background/60";
  return (
    <div data-testid={`feedback-row-${f.id}`} className={`flex justify-between gap-4 rounded-2xl border p-4 ${tone}`}>
      <div className="min-w-0 flex-1">
        <FeedbackMedia f={f} />
        <p className="text-xs mt-1 text-accent">
          — {f.name} · <span className="uppercase tracking-widest">{f.kind || "text"}</span>
          {pending && <span className="text-[#B57E17] font-medium"> · awaiting approval</span>}
          {hidden ? " · hidden" : ""} · {f.created_at ? new Date(f.created_at).toLocaleString() : ""}
        </p>
      </div>
      <div className="shrink-0 inline-flex gap-2 h-fit">
        {pending && (
          <button data-testid={`feedback-approve-${f.id}`} onClick={() => approveFeedback(f)} title="Approve & publish" className="inline-flex items-center gap-1.5 rounded-full bg-[#128C4B] text-white px-3 h-8 text-xs hover:-translate-y-0.5 transition-transform">
            <Check size={14} /> Approve
          </button>
        )}
        <button data-testid={`feedback-visible-${f.id}`} onClick={() => toggleFeedbackVisible(f)} title={hidden ? "Show on site" : "Hide from site"} className={iconBtn}>
          {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
        {f.kind === "text" && (
          <button data-testid={`feedback-edit-${f.id}`} onClick={() => startEditFb(f)} title="Edit" className={iconBtn}><Pencil size={14} /></button>
        )}
        <button data-testid={`delete-feedback-${f.id}`} onClick={() => deleteFeedback(f.id)} title="Delete" className="grid place-items-center w-8 h-8 rounded-full border border-destructive/40 text-destructive hover:bg-destructive hover:text-white transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const FilterBar = ({ filter, setFilter, counts }) => (
  <div className="inline-flex items-center gap-1 rounded-full border border-border bg-background/60 p-1" data-testid="feedback-status-filter">
    {Object.entries(FILTERS).map(([value, { label }]) => {
      const active = filter === value;
      return (
        <button key={value} type="button" data-testid={`feedback-filter-${value}`} onClick={() => setFilter(value)}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {label}
          <span data-testid={`feedback-filter-count-${value}`} className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[11px] font-medium ${active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-foreground/70"}`}>{counts[value]}</span>
        </button>
      );
    })}
  </div>
);

export const FeedbackManager = ({ feedbacks, ...actions }) => {
  const [filter, setFilter] = useState("pending");
  const pendingMedia = feedbacks.filter((f) => isPending(f) && (f.kind === "audio" || f.kind === "video")).length;
  const counts = { all: feedbacks.length, pending: pendingMedia, approved: feedbacks.filter(FILTERS.approved.match).length };
  const filtered = feedbacks.filter(FILTERS[filter].match);
  return (
    <div className="bg-[#FDF3EA] border border-[#F2DFCE] rounded-3xl p-6 mb-6" data-testid="feedback-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-serif text-2xl"><SubBadge /> Participant feedback ({filtered.length} of {feedbacks.length})</p>
        <div className="flex flex-wrap items-center gap-3">
          <FilterBar filter={filter} setFilter={setFilter} counts={counts} />
          {pendingMedia > 0 && (
            <span data-testid="pending-feedback-badge" className="inline-flex items-center gap-2 rounded-full border border-[#E0A63C]/50 bg-[#E0A63C]/10 px-4 py-2 text-sm font-medium text-[#8A5A00]">
              {pendingMedia} audio/video pending approval
            </span>
          )}
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="feedback-filter-empty">{FILTERS[filter].empty}</p>
      ) : (
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {filtered.map((f) => <FeedbackRow key={f.id} f={f} {...actions} />)}
        </div>
      )}
    </div>
  );
};
